import {
  MenuFold,
  ArrowRight,
  // Question,
  Logout,
  Close,
  ProfileUser,
  User,
  Pencil,
} from "@/components/icons/";
import {
  getMenuItems,
  type MenuItem,
  type MenuSubItem,
} from "@/core/navigation/menu";
import { useStore } from "@/store/index";
import { IconName } from "@/core/navigation/icons";
import { DashboardLayoutProps } from "@/types/layouts";
import { ICONS } from "@/core/navigation/icons";
import styles from "./DashboardLayout.module.scss";
import logoIcon from "@/assets/Logo.png";
import Cookies from "js-cookie";
import { removeTokens, getRefreshToken } from "@/utils/auth";
import { apiClient } from "@/lib/apiClient";
import { updateProfile, uploadProfilePhoto } from "@/lib/profile.api";
import Popover from "@/components/ui/popover";
import Drawer from "@/components/ui/drawer";
import Input from "@/components/ui/input";
import Button from "@/components/ui/button";
import Chip from "@/components/ui/chip";
import Modal from "@/components/ui/modal";
import FileInput from "@/components/ui/file-input";
import { toast } from "sonner";

import React, { useRef, useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { fetchCurrentUser, refreshCurrentUser } from "@/store/index";
import { usePermission } from "@/core/permissions/hooks";
import { isEducationManagerSession } from "@/core/auth/utils/education-manager-session";
import { isTrainerSession } from "@/core/auth/utils/trainer-session";
import ReactCrop, {
  type Crop,
  centerCrop,
  makeAspectCrop,
} from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n, t } = useTranslation();
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [, setSelectedLanguage] = useState(i18n.language);
  const [, setAvatarUrl] = useState<string | undefined>();
  const [, setIsAvatarLoading] = useState(true);
  const sidebarRef = useRef<HTMLElement | null>(null);
  const submenuRefs = useRef<Map<number, HTMLDivElement | null>>(new Map());
  const navItemLinkRefs = useRef<Map<number, HTMLDivElement | null>>(new Map());
  const currentUser = useStore((state) => state.currentUser);
  const userType = useStore((state) => state.userType);
  const isStaff = useStore((state) => state.isStaff);
  const { hasPermission } = usePermission();
  const [isMenuLoading, setIsMenuLoading] = useState(true);
  const [openSubmenuIndex, setOpenSubmenuIndex] = useState<null | number>(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isProfilePopoverOpen, setIsProfilePopoverOpen] = useState(false);
  const [isProfileDrawerOpen, setIsProfileDrawerOpen] = useState(false);
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<Crop>();
  const imgRef = useRef<HTMLImageElement>(null);
  const [isProfileSaving, setIsProfileSaving] = useState(false);
  const [isPhotoUploading, setIsPhotoUploading] = useState(false);
  const [editFirstName, setEditFirstName] = useState("");
  const [editLastName, setEditLastName] = useState("");
  const [editPhone, setEditPhone] = useState("");

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    setSelectedLanguage(lng);
    localStorage.setItem("selectedLanguage", lng);
  };

  const fetchProfile = async () => {
    setIsMenuLoading(true);

    try {
      // Force refresh: rol/yetki güncellemeleri anında yansısın.
      // Persist edilmiş eski permissionsArray'e takılıp cached kalmasın.
      await fetchCurrentUser(true);
      await refreshCurrentUser();
      setIsAvatarLoading(false);
    } catch (error) {
      console.error("🏠 Error fetching user profile and permissions:", error);
      setAvatarUrl("/assets/avatar.svg");
      setIsAvatarLoading(false);
    } finally {
      setTimeout(() => {
        setIsMenuLoading(false);
      }, 500);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  useEffect(() => {
    const savedLanguage = localStorage.getItem("selectedLanguage");
    if (savedLanguage) {
      changeLanguage(savedLanguage);
    } else {
      setSelectedLanguage(i18n.language);
    }
  }, [i18n.language]);

  useEffect(() => {
    if (currentUser) {
      let photoUrl = "/assets/avatar.svg";
      if (isStaff && currentUser.photo) {
        photoUrl = currentUser.photo;
      } else if (!isStaff && currentUser.staff?.photo) {
        photoUrl = currentUser.staff.photo;
      }
      setAvatarUrl(photoUrl);
    }
  }, [currentUser, isStaff]);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target as Node)
      ) {
        setSidebarOpen(false);
      }
    };

    if (isSidebarOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isSidebarOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (openSubmenuIndex !== null) {
        const submenuRef = submenuRefs.current.get(openSubmenuIndex);
        const navItemLinkRef = navItemLinkRefs.current.get(openSubmenuIndex);

        if (
          submenuRef &&
          navItemLinkRef &&
          !submenuRef.contains(target) &&
          !navItemLinkRef.contains(target)
        ) {
          setOpenSubmenuIndex(null);
        }
      }
    };

    if (openSubmenuIndex !== null) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openSubmenuIndex]);

  const menuItems = getMenuItems(isStaff, hasPermission)();

  const toggleSubmenu = (index: number) => {
    setOpenSubmenuIndex(openSubmenuIndex === index ? null : index);
  };

  const renderSidebarNav = () => {
    if (isMenuLoading) {
      return (
        <div className={styles.dashboardLayoutSidebarSkeleton}>
          {[...Array(8)].map((_, index) => (
            <div
              key={index}
              className={styles.dashboardLayoutSidebarSkeletonItem}
            >
              <div
                className={styles.dashboardLayoutSidebarSkeletonItemIcon}
              ></div>
              <div
                className={styles.dashboardLayoutSidebarSkeletonItemText}
              ></div>
            </div>
          ))}
        </div>
      );
    }

    // Normalize path by removing trailing slash for comparison
    const normalizePath = (path: string) => path.replace(/\/$/, "") || "/";
    const currentPath = normalizePath(location.pathname);

    const isTrainer = isTrainerSession(userType, currentUser);

    // Filter menu items based on permissions
    const filteredMenuItems = menuItems.filter((item: MenuItem) => {
      if (
        item.educationManagerOnly &&
        !isEducationManagerSession(userType, currentUser)
      ) {
        return false;
      }
      if (item.hideForTrainer && isTrainer) {
        return false;
      }
      // If item has submenu, show if at least one submenu item has permission
      if (item.submenu && item.submenu.length > 0) {
        const hasPermittedSubmenu = item.submenu.some(
          (subItem: MenuSubItem) => {
            if (!subItem.permission) return false;
            if (
              subItem.educationManagerOnly &&
              !isEducationManagerSession(userType, currentUser)
            ) {
              return false;
            }
            if (subItem.hideForTrainer && isTrainer) {
              return false;
            }
            return true;
          },
        );
        return hasPermittedSubmenu;
      }
      // For items without submenu, check their own permission
      return Boolean(item.permission);
    });

    return (
      <nav className={styles.dashboardLayoutSidebarNav}>
        <ul>
          {filteredMenuItems.map((item, index) => {
            const IconComponent = ICONS[item.icon as IconName];
            const hasSubmenu = item.submenu && item.submenu.length > 0;
            const isSubmenuOpen = openSubmenuIndex === index;

            // Filter submenu items based on permissions
            const filteredSubmenu = hasSubmenu
              ? item.submenu!.filter((subItem: MenuSubItem) => {
                  if (!subItem.permission) return false;
                  if (
                    subItem.educationManagerOnly &&
                    !isEducationManagerSession(userType, currentUser)
                  ) {
                    return false;
                  }
                  if (subItem.hideForTrainer && isTrainer) {
                    return false;
                  }
                  return true;
                })
              : [];

            // Active state logic - path prefix match for library/folder routes
            // Ana Sayfa (/dashboard) exact match only - alt sayfalar (education-management vb) ona eşlemesin
            const itemPath = normalizePath(item.link);
            const isMainItemActive = hasSubmenu
              ? filteredSubmenu.some(
                  (subItem: MenuSubItem) =>
                    normalizePath(subItem.link) === currentPath,
                )
              : currentPath === itemPath ||
                (itemPath !== "/" &&
                  itemPath !== "/dashboard" &&
                  currentPath.startsWith(itemPath + "/"));

            return (
              <div
                className={`${styles.dashboardLayoutSidebarNavItem} ${isMainItemActive ? styles.active : ""} ${isSubmenuOpen ? styles.open : ""}`}
                key={index}
              >
                <div
                  ref={(el) => {
                    if (el) navItemLinkRefs.current.set(index, el);
                    else navItemLinkRefs.current.delete(index);
                  }}
                  className={`${styles.dashboardLayoutSidebarNavItemLink} ${isSubmenuOpen ? styles.open : ""}`}
                  onClick={(e) => {
                    if (hasSubmenu) {
                      e.preventDefault();
                      const rect = e.currentTarget.getBoundingClientRect();
                      const submenu = e.currentTarget
                        .nextElementSibling as HTMLElement;
                      if (submenu) {
                        submenu.style.top = `${rect.top - 10}px`;
                      }
                      toggleSubmenu(index);
                    } else if (item.link) {
                      e.preventDefault();
                      navigate(item.link);
                    }
                  }}
                >
                  <div
                    className={styles.dashboardLayoutSidebarNavItemLinkContent}
                  >
                    <div
                      className={
                        styles.dashboardLayoutSidebarNavItemLinkContentIcon
                      }
                    >
                      {IconComponent && (
                        <IconComponent
                          className={styles.icon}
                          color="currentColor"
                          height={24}
                          width={24}
                        />
                      )}
                    </div>
                    <span>{item.name}</span>
                  </div>
                  {hasSubmenu && filteredSubmenu.length > 0 && (
                    <ArrowRight
                      className={`${styles.submenuArrow} ${isSubmenuOpen ? styles.active : ""}`}
                    />
                  )}
                  {isSidebarCollapsed && (
                    <div className={styles.tooltip}>{item.name}</div>
                  )}
                </div>

                {hasSubmenu && filteredSubmenu.length > 0 && (
                  <div
                    ref={(el) => {
                      if (el) submenuRefs.current.set(index, el);
                      else submenuRefs.current.delete(index);
                    }}
                    className={`${styles.dashboardLayoutSidebarNavSubmenu} ${isSubmenuOpen ? styles.open : ""}`}
                  >
                    {filteredSubmenu.map(
                      (subItem: MenuSubItem, subIndex: number) => {
                        const SubIconComponent =
                          ICONS[subItem.icon as IconName];
                        const isSubItemActive =
                          normalizePath(subItem.link) === currentPath;

                        return (
                          <div
                            className={`${styles.dashboardLayoutSidebarNavSubmenuItem} ${isSubItemActive ? styles.active : ""}`}
                            onClick={(e) => {
                              e.preventDefault();
                              if (subItem.link) {
                                navigate(subItem.link);
                                setOpenSubmenuIndex(null);
                              }
                            }}
                            key={subIndex}
                            style={{ cursor: "pointer" }}
                          >
                            <div
                              className={
                                styles.dashboardLayoutSidebarNavSubmenuItemLink
                              }
                            >
                              <div
                                className={
                                  styles.dashboardLayoutSidebarNavSubmenuItemLinkIcon
                                }
                              >
                                {SubIconComponent && (
                                  <SubIconComponent className={styles.icon} />
                                )}
                              </div>
                              <span>{subItem.name}</span>
                            </div>
                          </div>
                        );
                      },
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </ul>
      </nav>
    );
  };

  const getUserDisplayName = () => {
    if (!currentUser) return "";
    // Önce currentUser'ın kendi firstName/lastName alanlarını kontrol et
    const firstName = currentUser.firstName || currentUser.name || "";
    const lastName = currentUser.lastName || currentUser.surname || "";
    if (firstName || lastName) {
      return `${firstName} ${lastName}`.trim();
    }
    // Fallback: staff alanlarını kontrol et
    return `${currentUser.staff?.name || ""} ${currentUser.staff?.surname || ""}`.trim();
  };

  const getUserEmail = () => {
    if (!currentUser) return "";
    return currentUser.email || "";
  };

  const getUserTitle = () => {
    if (!currentUser) return "";
    return currentUser.title || "";
  };

  const isUserActive = () => {
    if (!currentUser) return false;
    return currentUser.isActive ?? true;
  };

  const getUserPhone = () => {
    if (!currentUser) return "";
    return currentUser.phoneNumber || "";
  };

  const getProfilePhotoUrl = () => {
    if (!currentUser) return "";
    return currentUser.profilePhotoUrl || "";
  };

  const openProfileDrawer = () => {
    const firstName = currentUser?.firstName || currentUser?.name || "";
    const lastName = currentUser?.lastName || currentUser?.surname || "";
    setEditFirstName(firstName);
    setEditLastName(lastName);
    setEditPhone(getUserPhone());
    setIsProfileDrawerOpen(true);
    setIsProfilePopoverOpen(false);
  };

  const getUserLdapUsername = () => {
    if (!currentUser) return "";
    if (currentUser.ldapUsername) return currentUser.ldapUsername;
    const name = currentUser.firstName || currentUser.name || "";
    const surname = currentUser.lastName || currentUser.surname || "";
    return `${name.toLowerCase()}_${surname.toLowerCase()}`.replace(
      /\s+/g,
      "_",
    );
  };

  const getUserDepartment = () => {
    if (!currentUser) return "";
    if (currentUser.department && typeof currentUser.department === "object") {
      return currentUser.department.name || "";
    }
    return "";
  };

  const renderProfilePopoverContent = () => (
    <div className={styles.profilePopoverContent}>
      <div className={styles.profilePopoverItem} onClick={openProfileDrawer}>
        <User width={20} height={20} />
        <span>{t("common.profile")}</span>
      </div>
      <div
        className={styles.profilePopoverItem}
        onClick={() => {
          handleLogout();
          setIsProfilePopoverOpen(false);
        }}
      >
        <Logout width={20} height={20} />
        <span>{t("common.logout")}</span>
      </div>
    </div>
  );

  const handlePhotoSelect = (files: File[]) => {
    if (files.length > 0) {
      const file = files[0];
      setSelectedPhoto(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target?.result) {
          setPhotoPreview(e.target.result as string);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const onImageLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const { width, height } = e.currentTarget;
      const cropSize = Math.min(width, height);
      const newCrop = centerCrop(
        makeAspectCrop(
          {
            unit: "px",
            width: cropSize * 0.8,
          },
          1,
          width,
          height,
        ),
        width,
        height,
      );
      setCrop(newCrop);
      setCompletedCrop(newCrop);
    },
    [],
  );

  const handlePhotoModalClose = () => {
    setIsPhotoModalOpen(false);
    setSelectedPhoto(null);
    setPhotoPreview(null);
    setCrop(undefined);
    setCompletedCrop(undefined);
  };

  const getCroppedImg = async (): Promise<File | null> => {
    const image = imgRef.current;
    if (!image || !completedCrop) return null;

    const canvas = document.createElement("canvas");
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    const pixelCrop = {
      x: completedCrop.x * scaleX,
      y: completedCrop.y * scaleY,
      width: completedCrop.width * scaleX,
      height: completedCrop.height * scaleY,
    };

    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    const ctx = canvas.getContext("2d");
    if (!ctx) return null;

    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height,
    );

    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            resolve(null);
            return;
          }
          const file = new File([blob], selectedPhoto?.name || "profile.jpg", {
            type: "image/jpeg",
          });
          resolve(file);
        },
        "image/jpeg",
        0.9,
      );
    });
  };

  const handlePhotoSave = async () => {
    if (!selectedPhoto || !completedCrop) return;

    setIsPhotoUploading(true);
    try {
      const croppedFile = await getCroppedImg();
      if (!croppedFile) {
        toast.error(t("common.errorOccurred"));
        return;
      }

      await uploadProfilePhoto(croppedFile);
      await refreshCurrentUser();
      toast.success(t("common.successfullySaved"));
      handlePhotoModalClose();
    } catch (error) {
      console.error("Photo upload error:", error);
      toast.error(t("common.errorOccurred"));
    } finally {
      setIsPhotoUploading(false);
    }
  };

  const handleProfileSave = async () => {
    setIsProfileSaving(true);
    try {
      await updateProfile({
        firstName: editFirstName,
        lastName: editLastName,
        phoneNumber: editPhone,
      });
      await refreshCurrentUser();
      toast.success(t("common.successfullySaved"));
      setIsProfileDrawerOpen(false);
    } catch (error) {
      console.error("Profile update error:", error);
      toast.error(t("common.errorOccurred"));
    } finally {
      setIsProfileSaving(false);
    }
  };

  const renderProfileDrawerFooter = () => (
    <div className={styles.profileDrawerFooter}>
      <Button
        variant="outlined"
        color="default"
        size="medium"
        onClick={() => setIsProfileDrawerOpen(false)}
        disabled={isProfileSaving}
      >
        {t("common.cancel")}
      </Button>
      <Button
        variant="contained"
        color="primary"
        size="medium"
        onClick={handleProfileSave}
        loading={isProfileSaving}
        disabled={isProfileSaving}
      >
        {t("common.save")}
      </Button>
    </div>
  );

  const renderPhotoModalContent = () => {
    if (photoPreview) {
      return (
        <div className={styles.photoPreviewContainer}>
          <button
            className={styles.photoPreviewRemove}
            onClick={() => {
              setSelectedPhoto(null);
              setPhotoPreview(null);
              setCrop(undefined);
              setCompletedCrop(undefined);
            }}
          >
            <Close width={16} height={16} color="#FFFFFF" />
          </button>
          <ReactCrop
            crop={crop}
            onChange={(c) => setCrop(c)}
            onComplete={(c) => setCompletedCrop(c)}
            aspect={1}
            circularCrop
            className={styles.photoCropper}
          >
            <img
              ref={imgRef}
              src={photoPreview}
              alt="Preview"
              onLoad={onImageLoad}
              className={styles.photoPreviewImage}
            />
          </ReactCrop>
        </div>
      );
    }

    return (
      <FileInput
        fileType="image"
        accept="image/*"
        supportedFormats={["JPG", "JPEG", "PNG", "GIF", "WEBP"]}
        maxSize={5}
        onFileSelect={handlePhotoSelect}
        placeholder={t("common.dragDropOrClick")}
        buttonLabel={t("common.addImage")}
      />
    );
  };

  const renderPhotoModalFooter = () => (
    <div className={styles.photoModalFooter}>
      <Button
        variant="outlined"
        color="default"
        size="medium"
        onClick={handlePhotoModalClose}
        disabled={isPhotoUploading}
      >
        {t("common.cancel")}
      </Button>
      <Button
        variant="contained"
        color="primary"
        size="medium"
        onClick={handlePhotoSave}
        disabled={!selectedPhoto || isPhotoUploading}
        loading={isPhotoUploading}
      >
        {t("common.save")}
      </Button>
    </div>
  );

  const renderSidebarFooter = () => {
    return (
      <div className={styles.dashboardLayoutSidebarFooter}>
        {!isSidebarCollapsed ? (
          <>
            {/* <div className={styles.sidebarFooterButton} onClick={handleHelp}>
              <Question width={24} height={24} color="#505766" />
              <span>{t("common.help")}</span>
            </div> */}
            <div className={styles.sidebarFooterProfileContainer}>
              <Popover
                isOpen={isProfilePopoverOpen}
                onClose={() => setIsProfilePopoverOpen(false)}
                content={renderProfilePopoverContent()}
                position="right"
              >
                <div
                  className={styles.sidebarFooterProfile}
                  onClick={() => setIsProfilePopoverOpen(!isProfilePopoverOpen)}
                >
                  <div className={styles.sidebarFooterProfileAvatar}>
                    {getProfilePhotoUrl() ? (
                      <img
                        src={getProfilePhotoUrl()}
                        alt={getUserDisplayName()}
                        className={styles.sidebarFooterProfileAvatarImage}
                      />
                    ) : (
                      <ProfileUser width={34} height={34} color="#72808F" />
                    )}
                  </div>
                  <div className={styles.sidebarFooterProfileInfo}>
                    <span className={styles.sidebarFooterProfileName}>
                      {getUserDisplayName() || t("common.user")}
                    </span>
                    <span className={styles.sidebarFooterProfileEmail}>
                      {getUserEmail()}
                    </span>
                  </div>
                </div>
              </Popover>
            </div>
          </>
        ) : (
          <>
            {/* <div
              className={styles.sidebarFooterIconButton}
              onClick={handleHelp}
              title={t("common.help")}
            >
              <Question width={24} height={24} />
            </div> */}
            <Popover
              isOpen={isProfilePopoverOpen}
              onClose={() => setIsProfilePopoverOpen(false)}
              content={renderProfilePopoverContent()}
              position="right"
            >
              <div
                className={`${styles.sidebarFooterIconButton} ${styles.sidebarFooterProfileIconButton}`}
                onClick={() => setIsProfilePopoverOpen(!isProfilePopoverOpen)}
                title={getUserDisplayName() || t("common.user")}
              >
                {getProfilePhotoUrl() ? (
                  <img
                    src={getProfilePhotoUrl()}
                    alt={getUserDisplayName()}
                    className={styles.sidebarFooterProfileAvatarImage}
                  />
                ) : (
                  <ProfileUser width={34} height={34} color="#72808F" />
                )}
              </div>
            </Popover>
          </>
        )}
      </div>
    );
  };

  const handleLogoClick = () => {
    navigate("/dashboard/");
  };

  const handleLogout = async () => {
    const refreshToken = getRefreshToken();
    try {
      if (refreshToken) {
        await apiClient.post("/api/auth/logout", { refreshToken });
      }
    } catch {
      // Logout API hata verse bile çıkış yap (token zaten silinecek)
    } finally {
      removeTokens();
      localStorage.removeItem("token");
      Cookies.remove("hiddenAnnouncements");
      useStore.getState().clearCurrentUser();
      navigate("/login");
    }
  };

  // const handleHelp = () => {
  //   // Add your help functionality here
  //   console.log("Help clicked");
  // };

  useEffect(() => {
    if (windowWidth <= 1450) setIsSidebarCollapsed(true);
    else setIsSidebarCollapsed(false);
  }, [windowWidth]);

  return (
    <div
      className={`${styles.dashboardLayout} ${isSidebarOpen ? styles.sidebarOpen : ""} ${isSidebarCollapsed ? styles.sidebarCollapsed : ""}`}
    >
      {isSidebarOpen && (
        <div
          className={styles.dashboardLayoutBackdrop}
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}

      <aside
        className={`${styles.dashboardLayoutSidebar} ${isSidebarOpen ? styles.open : ""} ${isSidebarCollapsed ? styles.collapsed : ""}`}
        ref={sidebarRef}
      >
        <div className={styles.dashboardLayoutSidebarContent}>
          <div className={styles.dashboardLayoutSidebarLogo}>
            <div className={styles.dashboardLayoutSidebarLogoContent}>
              <img alt="BOTAŞ" onClick={handleLogoClick} src={logoIcon} />
            </div>

            <button
              className={`${styles.collapseButton} ${isSidebarCollapsed ? styles.collapsed : ""}`}
              onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            >
              <MenuFold height={24} width={24} />
            </button>
          </div>
          {renderSidebarNav()}
        </div>
        {renderSidebarFooter()}
        {/* Mobile app links - customize as needed */}
        {/* <div className={styles.dashboardLayoutSidebarFooterOld}>
          <div className={styles.dashboardLayoutSidebarFooterItem}>
            <span className={styles.dashboardLayoutSidebarFooterItemTitle}>
              Download our mobile app
            </span>
            <a
              href="#"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.dashboardLayoutSidebarFooterItemButton}
            >
              <div className={styles.dashboardLayoutSidebarFooterItemButtonIcon}>
                <Apple />
                <span>App Store</span>
              </div>
              <div className={styles.dashboardLayoutSidebarFooterItemButtonArrow}>
                <ArrowRightFill width={16} height={16} />
              </div>
            </a>
            <a
              href="#"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.dashboardLayoutSidebarFooterItemButton}
            >
              <div className={styles.dashboardLayoutSidebarFooterItemButtonIcon}>
                <GooglePlay />
                <span>Google Play</span>
              </div>
              <div className={styles.dashboardLayoutSidebarFooterItemButtonArrow}>
                <ArrowRightFill width={16} height={16} />
              </div>
            </a>
          </div>
        </div> */}
      </aside>

      <div className={styles.dashboardLayoutMain}>
        <main className={styles.dashboardLayoutMainContent}>{children}</main>
      </div>

      <Drawer
        isOpen={isProfileDrawerOpen}
        onClose={() => setIsProfileDrawerOpen(false)}
        title={t("common.profile")}
        headerIcon={<User width={20} height={20} />}
        footer={renderProfileDrawerFooter()}
        width="420px"
      >
        <div className={styles.profileDrawerContent}>
          <div className={styles.profileDrawerHeader}>
            <div className={styles.profileDrawerHeaderAvatarWrapper}>
              <div className={styles.profileDrawerHeaderAvatar}>
                {getProfilePhotoUrl() ? (
                  <img
                    src={getProfilePhotoUrl()}
                    alt={getUserDisplayName()}
                    className={styles.profileDrawerHeaderAvatarImage}
                  />
                ) : (
                  <ProfileUser width={84} height={84} />
                )}
              </div>
              <button
                className={styles.profileDrawerHeaderEditButton}
                onClick={() => setIsPhotoModalOpen(true)}
              >
                <Pencil width={14} height={14} color="#707A8F" />
              </button>
            </div>
            <div className={styles.profileDrawerHeaderChip}>
              <Chip
                type={isUserActive() ? "success" : "default"}
                size="small"
                variant="filled"
              >
                {isUserActive() ? t("common.active") : t("common.inactive")}
              </Chip>
            </div>
            <div className={styles.profileDrawerHeaderInfo}>
              <h3 className={styles.profileDrawerHeaderName}>
                {getUserDisplayName()}
              </h3>
              <span className={styles.profileDrawerHeaderTitle}>
                {getUserTitle()}
              </span>
            </div>
          </div>

          <div className={styles.profileDrawerForm}>
            <Input
              label={t("common.name")}
              value={editFirstName}
              onChange={(e) => setEditFirstName(e.target.value)}
            />
            <Input
              label={t("common.surname")}
              value={editLastName}
              onChange={(e) => setEditLastName(e.target.value)}
            />
            <Input
              label={t("common.phoneNumber")}
              value={editPhone}
              onChange={(e) => setEditPhone(e.target.value)}
            />
            <Input
              label={t("common.ldapUsername")}
              value={getUserLdapUsername()}
              readOnly
              disabled
            />
            <Input
              label={t("common.department")}
              value={getUserDepartment()}
              readOnly
              disabled
            />
          </div>
        </div>
      </Drawer>

      <Modal
        isOpen={isPhotoModalOpen}
        onClose={handlePhotoModalClose}
        title={t("common.uploadProfilePhoto")}
        customFooter={renderPhotoModalFooter()}
        size="sm"
        customHeader={
          <div className={styles.photoModalHeader}>
            <div className={styles.photoModalHeaderIcon}>
              <User width={20} height={20} color="#E7212B" />
            </div>
            <h3>{t("common.uploadProfilePhoto")}</h3>
            <button
              className={styles.photoModalCloseButton}
              onClick={handlePhotoModalClose}
            >
              <Close width={20} height={20} color="#34314A" />
            </button>
          </div>
        }
      >
        {renderPhotoModalContent()}
      </Modal>
    </div>
  );
};

export default DashboardLayout;
